import * as React from 'react';

import { Box, Tab, Tabs, Stack, useTheme, Typography, useMediaQuery } from '@mui/material';

import { useCustomNavigate } from 'src/hooks/use-custom-navigate';

import Iconify from 'src/components/iconify';

import { useTabs } from './tabs.hook';

export interface TabsProps {
  permission?: string;
  onNavigate?: () => void;
}

export default function HeaderTabs({ permission, onNavigate }: TabsProps) {
  const navigate = useCustomNavigate();
  const theme = useTheme();
  const { value, handleChange } = useTabs(permission ?? '');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDown365 = useMediaQuery('(max-width: 365px)');
  const isDown300 = useMediaQuery('(max-width: 300px)');

  const tabsConfig = [
    {
      label: 'MINHA CONTA',
      mobileLabel: 'Perfil',
      icon: 'material-symbols:person-edit-outline-rounded',
      path: '/user/profile',
    },
    {
      label: 'MEUS BENEFÍCIOS',
      mobileLabel: isDown300 ? 'Benefícios' : 'Meus Benefícios',
      icon: 'tabler:gift',
      path: '/benefits',
    },
    {
      label: 'INÍCIO',
      mobileLabel: 'Início',
      icon: 'fluent:home-16-regular',
      path: '/home',
    },
  ];

  const handleTabClick = (event: React.SyntheticEvent, newValue: number) => {
    handleChange(event, newValue);
    navigate(tabsConfig[newValue].path);
    if (onNavigate) onNavigate();
  };

  const calcMobileTabBox = (isSelected: boolean) => {
    if (isDown300) {
      if (isSelected) {
        return '28px';
      }
      return '24px';
    }
    if (isSelected) {
      return '40px';
    }
    return 'clamp(24px, 20dvw, 28px)';
  };

  const calcMobileTabIconify = (isSelected: boolean) => {
    if (isDown300) {
      if (isSelected) {
        return '20px';
      }
      return '16px';
    }
    if (isSelected) {
      return '24px';
    }
    return 'clamp(12px, 6vw, 20px)';
  };

  return (
    <Box
      data-testid="tabs-render-test"
      sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <Tabs
        value={value}
        onChange={handleTabClick}
        orientation="horizontal"
        role="navigation"
        aria-label="tabs navegação"
        TabIndicatorProps={{ sx: { display: 'none' } }}
        sx={{
          height: '100%',
          bgcolor: 'transparent',
          '& .MuiTabs-flexContainer': {
            flexDirection: 'row',
            width: 'auto',
          },
          justifyContent: 'center',
        }}
      >
        {tabsConfig.map((tab, index) => {
          const isSelected = value === index;

          return isMobile ? (
            <Tab
              key={index}
              label={
                <Stack direction="column" alignItems="center" justifyContent="center" spacing={0.5}>
                  <Box
                    sx={{
                      width: calcMobileTabBox(isSelected),
                      height: calcMobileTabBox(isSelected),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      border: '1px solid',
                      borderColor: isSelected ? 'transparent' : 'primaryButtons.bgColor',
                      backgroundColor: 'transparent',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Iconify
                      icon={tab.icon}
                      width={calcMobileTabIconify(isSelected)}
                      height={calcMobileTabIconify(isSelected)}
                      sx={{ opacity: '60%' }}
                      color={isSelected ? '#EAEEF5' : 'primaryButtons.bgColor'}
                    />
                  </Box>

                  {!isSelected && (
                    <Typography
                      sx={{
                        fontSize: 'clamp(10px, 1.5vw, 12px)',
                        fontWeight: 500,
                        color: '#959595',
                        visibility: isSelected ? 'hidden' : 'visible',
                        lineHeight: 1,
                      }}
                    >
                      {tab.mobileLabel}
                    </Typography>
                  )}
                </Stack>
              }
              sx={{
                color: isSelected ? 'primaryButtons.textColor' : 'primaryButtons.bgColor',
                py: '3px',
                borderRadius: '8px',
                m: isDown365
                  ? `0 ${index === tabsConfig.length - 1 ? '0px' : '5px'} 0 0 !important`
                  : `0 ${index === tabsConfig.length - 1 ? '0px' : '16px'} 0 0 !important`,
                bgcolor: isSelected ? 'primaryButtons.bgColor' : 'transparent',
                transition: 'all 0.3s ease',
                mx: 0,
                px: '0px !important',
              }}
            />
          ) : (
            <Tab
              key={index}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      border: '1px solid',
                      borderColor: isSelected ? 'common.white' : 'primaryButtons.bgColor',
                      backgroundColor: 'transparent',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <Iconify
                      icon={tab.icon}
                      width={20}
                      height={20}
                      sx={{ opacity: isSelected ? '100%' : '60%' }}
                      color={isSelected ? 'primaryButtons.textColor' : 'primaryButtons.bgColor'}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: 'clamp(16px, 2vw, 24px)',
                      fontWeight: 400,
                      fontFamily: 'Bebas Neue, Inter',
                      color: isSelected ? 'primaryButtons.textColor' : 'primaryButtons.bgColor',
                      textTransform: 'uppercase',
                    }}
                  >
                    {tab.label}
                  </Typography>
                </Box>
              }
              sx={{
                color: isSelected ? 'primaryButtons.textColor' : 'primaryButtons.bgColor',
                minHeight: 32,
                px: '16px',
                py: '8px',
                borderRadius: '8px',
                bgcolor: isSelected ? 'primaryButtons.bgColor' : 'transparent',
                transition: 'all 0.3s ease',
                m: index !== tabsConfig.length - 1 ? '0 32px 0 0 !important' : '0 0 0 0 !important',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            />
          );
        })}
      </Tabs>
    </Box>
  );
}
